/**
 * Chat Admin Service
 * Provides API calls for chat admin panel to view user database logs
 * 
 * ADMIN ONLY - FOR USER DATABASE MONITORING
 * Access restricted to chat admins (paymanshu.sharma@maersk.com)
 */

import { getAuthHeaders } from './authHeaders';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

async function jsonHeaders(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

// ============================================================================
// Types (aligned with simplified PostgreSQL schema)
// ============================================================================

export type UserType = 'global' | 'eme' | 'nam';

export interface User {
  email: string;
  email_hash: string;
  display_name?: string;
  last_login?: string;
  is_waitlist?: boolean;
  is_authorized?: boolean | null;
  user_type?: UserType;
}

export interface WhitelistEntry {
  email: string;
  email_hash: string;
  is_authorized: boolean;
  user_type?: UserType;
}

export interface ConversationMessage {
  message_id: number;  // BIGINT
  email: string;
  email_hash: string;
  conversation_id: number;  // BIGINT
  job_id: string;
  user_query_text: string;
  query_started_at: string;
  response_status?: string;
  response_latest_update?: string;
  citation_status?: string;
  execution_time_ms?: number;
  generated_response?: string;  // LLM response text
  sql_query?: string;  // Generated SQL query
}

export interface ChatAdminStats {
  total_users: number;
  total_authorized_users: number;
  total_conversations: number;
  conversations_by_status: { [key: string]: number };
  conversations_by_citation: { [key: string]: number };
  avg_execution_time_ms?: number;
  waitlisted_users: number;
  users_pending_access: number;
  conversations_with_response: number;
  conversations_without_response: number;
  avg_response_length?: number;
  conversations_per_day: { day: string; count: number }[];
  most_active_users: (User & { query_count: number })[];
}

export interface UserDetails {
  user_info: User;
  whitelist_status: WhitelistEntry | null;
  recent_messages: ConversationMessage[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get all users from cc_ai.users table
 */
export async function chatAdminGetAllUsers(
  adminEmail: string,
  options?: {
    limit?: number;
    offset?: number;
    search_email?: string;
  }
): Promise<User[]> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/get-users`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        limit: options?.limit,
        offset: options?.offset || 0,
        search_email: options?.search_email,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch users');
    }

    const data = await response.json();
    return data.users;
  } catch (error: any) {
    console.error('Error fetching users:', error);
    throw error;
  }
}

/**
 * Get all authorized users from user_whitelist table
 */
export async function chatAdminGetAuthorizedUsers(
  adminEmail: string,
  options?: {
    limit?: number;
    offset?: number;
    search_email?: string;
  }
): Promise<WhitelistEntry[]> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/get-authorized-users`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        limit: options?.limit,
        offset: options?.offset || 0,
        search_email: options?.search_email,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch authorized users');
    }

    const data = await response.json();
    return data.entries;
  } catch (error: any) {
    console.error('Error fetching authorized users:', error);
    throw error;
  }
}

/**
 * Get all conversation messages (queries) from conversations table
 */
export async function chatAdminGetConversations(
  adminEmail: string,
  options?: {
    user_email_filter?: string;
    status_filter?: string;
    limit?: number;
    offset?: number;
  }
): Promise<ConversationMessage[]> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/get-conversations`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        user_email_filter: options?.user_email_filter,
        status_filter: options?.status_filter,
        limit: options?.limit,
        offset: options?.offset || 0,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch conversations');
    }

    const data = await response.json();
    return data.messages;
  } catch (error: any) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
}

/**
 * Remove login events API (no longer exists in simplified schema)
 */

/**
 * Get aggregate statistics from the user database
 */
export async function chatAdminGetStats(adminEmail: string): Promise<ChatAdminStats> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/get-stats`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch stats');
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching stats:', error);
    throw error;
  }
}

/**
 * Get detailed information for a specific user
 */
export async function chatAdminGetUserDetails(
  adminEmail: string,
  userEmail: string
): Promise<UserDetails> {
  try {
    const response = await fetch(
      `${API_URL}/chat/admin/user-details/${encodeURIComponent(userEmail)}?admin_email=${encodeURIComponent(adminEmail)}`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch user details');
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching user details:', error);
    throw error;
  }
}

/**
 * Add or update user in whitelist
 * Follows migration script pattern:
 * 1. Creates user in cc_ai.users table (if doesn't exist)
 * 2. Adds to cc_ai.user_whitelist table
 */
export async function chatAdminUpdateWhitelist(
  adminEmail: string,
  userEmail: string,
  isAuthorized: boolean = true,
  userType?: UserType
): Promise<any> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/update-whitelist`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        email: userEmail,
        is_authorized: isAuthorized,
        user_type: userType || 'global',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update whitelist');
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error updating whitelist:', error);
    throw error;
  }
}

/**
 * Update user details (e.g. display_name, user_type)
 */
export async function chatAdminUpdateUser(
  adminEmail: string,
  userEmail: string,
  updates: {
    display_name?: string;
    user_type?: UserType;
  }
): Promise<any> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/update-user`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        email: userEmail,
        display_name: updates.display_name,
        user_type: updates.user_type,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update user');
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error updating user:', error);
    throw error;
  }
}

/**
 * Remove user from whitelist
 */
export async function chatAdminRemoveWhitelist(
  adminEmail: string,
  userEmail: string
): Promise<any> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/remove-whitelist`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        email: userEmail,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove from whitelist');
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error removing from whitelist:', error);
    throw error;
  }
}

/**
 * Export all user database data as JSON
 */
export async function chatAdminExportData(adminEmail: string): Promise<any> {
  try {
    const response = await fetch(
      `${API_URL}/chat/admin/export-data?admin_email=${encodeURIComponent(adminEmail)}`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to export data');
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error exporting data:', error);
    throw error;
  }
}

/**
 * Import user database data from JSON (appends to existing data)
 */
export async function chatAdminImportData(adminEmail: string, data: any): Promise<any> {
  try {
    const response = await fetch(`${API_URL}/chat/admin/import-data`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        data: data,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to import data');
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error importing data:', error);
    throw error;
  }
}
